/**
 * CalendarPageTouristTaxBanner Component
 * 
 * Purpose:
 * Tourist tax banner component that displays the monthly tourist tax sum.
 * Always renders; shows "$0.00" when zero. Provides "View details" to tourist tax ledger.
 * 
 * High-level Workflow:
 * 1. Receives monthlyTouristTaxSum, isMobile, and navigation handler as props.
 * 2. Conditionally renders only when monthlyTouristTaxSum > 0.
 * 3. Displays formatted currency amount and subtext ("Typically withdrawn on the 10th").
 * 4. Provides "View details →" button that navigates to financials page with tourist-tax-ledger hash.
 * 5. Applies mobile/desktop styling based on isMobile prop.
 * 
 * Assumptions & Invariants:
 * - Always visible: Renders even when sum is zero; displays "$0.00" in that case.
 * - Currency formatting: Uses formatCurrency utility to format monthlyTouristTaxSum for display.
 * - Navigation: "View details →" button navigates to '/financials#tourist-tax-ledger' using
 *   navigate handler from react-router-dom. Button has keyboard support (Enter/Space).
 * - Hover effects: Button has onMouseEnter/onMouseLeave handlers that change background and
 *   text color for visual feedback.
 * - Mobile/desktop styling: Uses different style functions (getTouristTaxBannerStyleMobile vs
 *   touristTaxBanner) based on isMobile prop.
 * 
 * Dependencies:
 * - Internal:
 *   - utils/calendarFormatting.js: formatCurrency utility function
 * - External:
 *   - react: None (functional component, no hooks)
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 5 refactoring)
 */

import { formatCurrency } from '../../utils/calendarFormatting.jsx';
import Tooltip from '../shared/Tooltip.jsx';

const TOURIST_TAX_HELP_URL = 'https://www.dropbox.com/scl/fo/wulsbqkj77x923xgll1vq/ACCDpsLyhtnW57YK9oRRayM/To%20Use?e=2&preview=Understand+Tourist+Taxes.pdf&rlkey=xb3uks5p246lmkhewb4ji4kv9&subfolder_nav_tracking=1&dl=0';
const TOURIST_TAX_TOOLTIP = 'Tourist Tax Filing\n\nHow tourist taxes are calculated, filed, and paid.';

/**
 * Tourist tax banner component
 * 
 * Always renders. Shows "Tourist taxes due this month: $X.XX" (including $0.00 when zero).
 * 
 * @param {boolean} isMobile - Whether mobile layout is active
 * @param {number} monthlyTouristTaxSum - Monthly tourist tax sum (use 0 when none)
 * @param {Function} navigate - Navigation handler from react-router-dom
 * @param {Object} styles - Styles object from CalendarPage
 */
export default function CalendarPageTouristTaxBanner({
  isMobile,
  monthlyTouristTaxSum,
  navigate,
  styles,
}) {
  const amount = typeof monthlyTouristTaxSum === 'number' && !Number.isNaN(monthlyTouristTaxSum)
    ? monthlyTouristTaxSum
    : 0;

  return (
    <div 
      className="owner-section-panel" 
      data-component="tourist-tax-banner"
      style={isMobile ? styles.getTouristTaxBannerStyleMobile() : styles.touristTaxBanner}
    >
      <div style={isMobile ? styles.getTouristTaxBannerContentMobile() : styles.touristTaxBannerContent}>
        <div style={styles.touristTaxBannerLeft}>
          <div style={isMobile ? styles.getTouristTaxBannerTitleMobile() : styles.touristTaxBannerTitle}>
            Tourist taxes due this month: {formatCurrency(amount)}
          </div>
          <div style={isMobile ? styles.getTouristTaxBannerSubtextMobile() : styles.touristTaxBannerSubtext}>
            Tourist taxes are calculated on the check-in date and are filed and paid in the following month by the 10th.
            {!isMobile && (
              <Tooltip
                content={TOURIST_TAX_TOOLTIP}
                href={TOURIST_TAX_HELP_URL}
                target="_blank"
                rel="noopener noreferrer"
                ariaLabel="Tourist tax filing information"
              >
                <span style={{ marginLeft: 'var(--spacing-1)', opacity: 0.8, fontSize: 'var(--font-size-xs)', display: 'inline-flex', verticalAlign: 'middle' }} aria-hidden="true">
                  ⓘ
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <button
          onClick={() => navigate('/financials?tab=payments-taxes')}
          style={isMobile ? styles.getTouristTaxBannerButtonMobile() : styles.touristTaxBannerButton}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/financials?tab=payments-taxes');
            }
          }}
          aria-label="View tourist tax details"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-status-warning-base, #f59e0b)';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--color-status-warning-text, #92400e)';
          }}
        >
          View details →
        </button>
      </div>
    </div>
  );
}
